import React, { useState, useEffect, useCallback } from 'react';
import { dataService } from '../../services/dataService';
import { normaliseSchedule, scheduleLabel, ScheduleEditor } from '../Pipeline/ExecutionPanel';
import KnowledgePanel from './KnowledgePanel';
import { tierPricing, estimateTypicalTask, formatUsd, spendBand, SPEND_BAND_META, functionSpend } from '../../utils/agentCost';
import { useWorkspaceRole } from '../../contexts/WorkspaceRoleContext';
import { auth } from '../../firebaseConfig';

function loadPipelineSchedule(workspaceId) {
  try {
    const d = JSON.parse(localStorage.getItem(`pipelineExecution_${workspaceId}`));
    return d?.scheduleConfig || null;
  } catch { return null; }
}

function isInSchedule(schedule) {
  const s = normaliseSchedule(schedule);
  if (!s || s.type === 'triggered' || s.type === 'always') return true;
  const now = new Date();
  const day = now.getDay() === 0 ? 7 : now.getDay();
  if (!(s.days || []).includes(day)) return false;
  const t = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  return t >= (s.from || '09:00') && t <= (s.to || '17:00');
}

const STATUS_CONFIG = {
  idle:            { label: 'Idle',            color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
  paused_manual:   { label: 'Paused',          color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
  paused_schedule: { label: 'Out of schedule', color: '#6366f1', bg: 'rgba(99,102,241,0.1)' },
  inactive:        { label: 'Inactive',        color: '#94a3b8', bg: 'rgba(148,163,184,0.1)' },
};

const MODEL_LABELS = {
  fast:     '⚡ Efficient',
  standard: '◈ Balanced',
  smart:    '★ Expert',
};

function getAgentStatus(agent, fnNode, pipelineSchedule) {
  if (fnNode.data.active === false) return 'inactive';
  if (agent.paused) return 'paused_manual';
  const eff = fnNode.data.schedule || pipelineSchedule;
  if (!isInSchedule(eff)) return 'paused_schedule';
  return 'idle';
}

export default function AgentsPage({ workspaceId, onNavigate, opsMode }) {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pipelineSchedule, setPipelineSchedule] = useState(null);
  const [editingScheduleFor, setEditingScheduleFor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('agents');
  const { hasCapability } = useWorkspaceRole() || {};

  useEffect(() => {
    if (!workspaceId) return;
    setPipelineSchedule(loadPipelineSchedule(workspaceId));
    dataService.getPipeline(workspaceId).then(data => {
      setNodes(data.nodes || []);
      setEdges(data.edges || []);
      setLoading(false);
    });
  }, [workspaceId]);

  const persist = useCallback(async (updatedNodes) => {
    setSaving(true);
    await dataService.savePipeline(workspaceId, updatedNodes, edges);
    setSaving(false);
  }, [workspaceId, edges]);

  const toggleAgentPause = useCallback((fnNodeId, agentId) => {
    setNodes(prev => {
      const updated = prev.map(n => {
        if (n.id !== fnNodeId) return n;
        return {
          ...n, data: {
            ...n.data,
            agents: n.data.agents.map(a => a.id === agentId ? { ...a, paused: !a.paused } : a)
          }
        };
      });
      persist(updated);
      return updated;
    });
  }, [persist]);

  const saveFnSchedule = useCallback((fnNodeId, schedule) => {
    setNodes(prev => {
      const updated = prev.map(n =>
        n.id === fnNodeId ? { ...n, data: { ...n.data, schedule } } : n
      );
      persist(updated);
      return updated;
    });
    setEditingScheduleFor(null);
  }, [persist]);

  const functionNodes = nodes.filter(n => n.type === 'functionNode');

  if (!workspaceId) return <div className="agents-page-empty"><p>No workspace selected.</p></div>;
  if (loading) return <div className="agents-page-empty"><p>Loading agents…</p></div>;

  const canEditKnowledge = hasCapability ? hasCapability('pipeline-edit') : true;
  const tabs = (
    <div className="agents-tabs">
      <button className={`agents-tab${tab === 'agents' ? ' active' : ''}`} onClick={() => setTab('agents')}>
        {opsMode ? 'Employees' : 'Agents'}
      </button>
      <button className={`agents-tab${tab === 'knowledge' ? ' active' : ''}`} onClick={() => setTab('knowledge')}>
        Knowledge
      </button>
    </div>
  );

  if (tab === 'knowledge') return (
    <div className="agents-page">
      {tabs}
      <KnowledgePanel
        workspaceId={workspaceId}
        nodes={nodes}
        canEdit={canEditKnowledge}
        currentUser={auth.currentUser?.email || null}
      />
    </div>
  );

  if (functionNodes.length === 0) return (
    <div className="agents-page">
      {tabs}
      <div className="agents-page-empty">
        <div className="agents-empty-icon">🤖</div>
        <p>No {opsMode ? 'department' : 'function'} nodes in your pipeline yet.</p>
        <button className="agents-go-btn" onClick={() => onNavigate(opsMode ? 'operations' : 'pipeline')}>
          Go to {opsMode ? 'Operations' : 'Pipeline'}
        </button>
      </div>
    </div>
  );

  // Each dot is relative to the others, so the whole set has to be known first.
  // Until an agent has actually run there is no spend to compare, so the tier's
  // estimated cost per task stands in — it is the only signal that exists, and
  // it is the thing the user just chose.
  const agentFigure = (agent) => estimateTypicalTask(agent.model);
  const allFigures = functionNodes.flatMap(fn => (fn.data.agents || []).map(agentFigure));

  const totalAgents = functionNodes.reduce((s, fn) => s + (fn.data.agents?.length || 0), 0);
  const pausedAgents = functionNodes.reduce((s, fn) =>
    s + (fn.data.agents || []).filter(a => getAgentStatus(a, fn, pipelineSchedule) !== 'idle').length, 0);

  return (
    <div className="agents-page">
      {tabs}
      <div className="agents-page-header">
        <div>
          <h1 className="agents-page-title">{opsMode ? 'Employees' : 'Agents'}</h1>
          <div className="agents-page-sub">
            {totalAgents} {opsMode ? 'employee' : 'agent'}{totalAgents !== 1 ? 's' : ''} across {functionNodes.length} {opsMode ? 'department' : 'function'}{functionNodes.length !== 1 ? 's' : ''}
            {pausedAgents > 0 && <span className="agents-paused-note"> · {pausedAgents} paused</span>}
          </div>
        </div>
        <div className="agents-pipeline-schedule">
          <span className="agents-pipeline-sched-label">Pipeline schedule:</span>
          <span className="agents-pipeline-sched-val">{scheduleLabel(normaliseSchedule(pipelineSchedule)) || 'Triggered only'}</span>
          {saving && <span className="agents-saving">Saving…</span>}
        </div>
      </div>

      <div className="agents-fn-list">
        {functionNodes.map(fn => {
          const fnSchedule = fn.data.schedule;
          const isEditing = editingScheduleFor === fn.id;
          const schedSummary = fnSchedule
            ? scheduleLabel(normaliseSchedule(fnSchedule))
            : null;

          return (
            <div key={fn.id} className="agents-fn-card">
              <div className="agents-fn-header">
                <div className="agents-fn-title-row">
                  <div className="agents-fn-name-wrap">
                    <span className="agents-fn-name">{fn.data.label}</span>
                    {fn.data.active === false && <span className="agents-fn-inactive-tag">Inactive</span>}
                    {/* The team total is the sum of its agents, computed rather
                        than stored, so it can never disagree with the rows
                        below it — the quickest way a cost display loses trust. */}
                    {(fn.data.agents || []).length > 0 && (() => {
                      const spend = functionSpend(fn.data.agents || []);
                      return (
                        <span
                          className="agents-fn-cost"
                          title={`Estimated cost if every agent on this team ran one task. Actual spend replaces this once agents have run.`}
                        >
                          ≈{formatUsd(spend.totalUsd)} / round (est.)
                        </span>
                      );
                    })()}
                  </div>
                  <div className="agents-fn-actions">
                    <span className="agents-fn-sched-badge">
                      {schedSummary ? `Custom: ${schedSummary}` : 'Inherits pipeline schedule'}
                    </span>
                    <button
                      className={`agents-fn-sched-btn${isEditing ? ' active' : ''}`}
                      onClick={() => setEditingScheduleFor(isEditing ? null : fn.id)}
                    >
                      ⏱ {isEditing ? 'Cancel' : 'Edit schedule'}
                    </button>
                  </div>
                </div>
                {fn.data.description && <div className="agents-fn-desc">{fn.data.description}</div>}

                {isEditing && (
                  <div className="agents-sched-editor-wrap">
                    <div className="agents-sched-editor-title">Function schedule</div>
                    <div className="agents-sched-editor-hint">
                      Set a custom schedule for this {opsMode ? 'department' : 'function'}, or reset to inherit the pipeline schedule.
                    </div>
                    <ScheduleEditor
                      value={fn.data.schedule || null}
                      onChange={(s) => {
                        setNodes(prev => prev.map(n =>
                          n.id === fn.id ? { ...n, data: { ...n.data, schedule: s } } : n
                        ));
                      }}
                      allowNull
                    />
                    <div className="agents-sched-editor-actions">
                      <button
                        className="agents-sched-save-btn"
                        onClick={() => saveFnSchedule(fn.id, nodes.find(n => n.id === fn.id)?.data.schedule || null)}
                      >
                        Save
                      </button>
                      <button
                        className="agents-sched-clear-btn"
                        onClick={() => saveFnSchedule(fn.id, null)}
                      >
                        Reset to pipeline
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="agents-list">
                {(fn.data.agents || []).length === 0 && (
                  <div className="agents-fn-empty">No {opsMode ? 'employees' : 'agents'} assigned yet.</div>
                )}
                {(fn.data.agents || []).map(agent => {
                  const status = getAgentStatus(agent, fn, pipelineSchedule);
                  const sc = STATUS_CONFIG[status];
                  return (
                    <div key={agent.id} className="agent-row">
                      <div className="agent-row-left">
                        <div className="agent-emoji">{agent.emoji || '🧑'}</div>
                        {(() => {
                          const band = spendBand(agentFigure(agent), allFigures);
                          const meta = SPEND_BAND_META[band];
                          return (
                            <span
                              className={`agent-spend-dot agent-spend-${band}`}
                              style={{ background: meta.color }}
                              title={`${meta.label} — ≈${formatUsd(agentFigure(agent))} a task at the ${tierPricing(agent.model).label} tier (estimate)`}
                            />
                          );
                        })()}
                        <div className="agent-info">
                          <div className="agent-name-row">
                            <span className="agent-name">{agent.name}</span>
                            {agent.isManager && (
                              <span className="agent-manager-tag">{opsMode ? 'Head' : 'Manager'}</span>
                            )}
                          </div>
                          {agent.role && <div className="agent-role">{agent.role}</div>}
                          <div className="agent-model-badge">
                            {MODEL_LABELS[agent.model] || MODEL_LABELS.standard}
                          </div>
                          {/* Tier choice moves cost by roughly 5x end to end, so
                              show what it implies before an agent has ever run.
                              Labelled an estimate because real spend varies with
                              the size of the job far more than with the tier. */}
                          <div className="agent-cost-hint" title="Rough cost of one moderate task at this tier. Actual spend depends far more on the size of the job.">
                            {tierPricing(agent.model).label} rate ·
                            {' '}${tierPricing(agent.model).inputPerM}/${tierPricing(agent.model).outputPerM} per M tokens
                            {' '}· ≈{formatUsd(estimateTypicalTask(agent.model))} a task (est.)
                          </div>
                        </div>
                      </div>
                      <div className="agent-row-right">
                        <span className="agent-status-badge" style={{ color: sc.color, background: sc.bg }}>
                          <span className="agent-status-dot" style={{ background: sc.color }} />
                          {sc.label}
                        </span>
                        <button
                          className={`agent-pause-btn${agent.paused ? ' resume' : ''}`}
                          onClick={() => toggleAgentPause(fn.id, agent.id)}
                          title={agent.paused ? 'Resume agent' : 'Pause agent'}
                        >
                          {agent.paused ? '▶ Resume' : '⏸ Pause'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
